'use client'
import type React from 'react'
import { createContext } from 'react'

import type { ContactInfo, TrimmedAccountFragment, TrimmedAccountWithContactInfoFragment } from '~/generated/graphql'
import { accountRoute, appTrace } from '~/utils'
import { useNavigation } from '../auth/NavigationContext'

export type PeerAccountContextType = {
  account: TrimmedAccountFragment | TrimmedAccountWithContactInfoFragment
  navigateToProfile: (e: React.MouseEvent) => void
  navigateToProfileNew: () => void
}

export const PeerAccountContext = createContext<PeerAccountContextType>({} as PeerAccountContextType)

export function hasAccountContactInfo(
  account: TrimmedAccountFragment | TrimmedAccountWithContactInfoFragment
): account is TrimmedAccountWithContactInfoFragment & { contactInfo: ContactInfo } {
  return !!(account as TrimmedAccountWithContactInfoFragment).contactInfo
}

export function PeerAccountProvider({
  account,
  children,
}: React.PropsWithChildren & { account: TrimmedAccountFragment }) {
  const navigation = useNavigation()

  const routeInfo = {
    show: accountRoute(account.id).show,
    connect: accountRoute(account.id).connect,
  }

  function navigateToProfile(e: React.MouseEvent) {
    // Only allow navigation if clicking on specific elements
    const allowedElements = ['IMG', 'P', 'H6', 'SPAN', 'H1']
    if (e.target && !allowedElements.includes((e.target as HTMLElement).tagName)) {
      appTrace('click on incorrect target', e.target)
      return
    }
    appTrace('navigating to profile', routeInfo.show)
    navigation.navigate({
      name: 'peer_account_profile',
      route: routeInfo.show,
      event: e,
    })
  }

  function navigateToProfileNew() {
    navigation.navigate({
      name: 'peer_account_profile_new',
      route: routeInfo.show,
    })
  }

  return (
    <PeerAccountContext.Provider value={{ account, navigateToProfile, navigateToProfileNew }}>
      {children}
    </PeerAccountContext.Provider>
  )
}
